import * as React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Avatar,
  Button,
  Badge,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  EmptyState,
  ErrorState,
  Skeleton,
  SkeletonText,
  StatusPill,
  PriorityPill,
  Tabs,
  TagList,
  SlideOver,
  RichTextEditor,
  Tooltip,
  useToast,
  useConfirm,
} from '@/components/ui';
import { SlaClockPanel, validateSlaLifecycleState, type SlaLifecycleState, type SlaMetricObligation } from '@/components/tickets/sla-clock';
import { ReplyComposer, type ReplySentEvent } from '@/components/tickets/reply-composer';
import { AttachmentPreview } from '@/components/tickets/attachment-preview';
import { SidebarRow } from '@/components/tickets/sidebar-row';
import { JiraLinksCard } from '@/components/tickets/jira-links-card';
import { ConversationActions } from '@/components/tickets/conversation-actions';
import { ThreadDrawer } from '@/components/tickets/thread-drawer';
import { ForwardDrawer } from '@/components/tickets/forward-drawer';
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui/searchable-select';
import {
  Pencil,
  UserRound,
  Ticket as TicketIcon,
  Puzzle,
  MessageSquare,
  BookOpen,
  CalendarClock,
  PanelRightClose,
  PanelRightOpen,
  PanelLeftClose,
  PanelLeftOpen,
  Download,
  Clock,
} from 'lucide-react';
import { DynamicTicketFieldRenderer, type DynamicTicketField } from '@/components/forms/DynamicTicketFieldRenderer';
import { api, ApiError } from '@/lib/api-client';
import { cn } from '@/lib/cn';
import { useAttachmentUploader } from '@/lib/attachment-uploader';
import { getTicketFields, type TicketFieldRegistryField } from '@/lib/ticket-field-registry-api';
import { TICKET_CLOSURE_REQUIRED_FIELDS, validateTicketClosureFields } from '@/lib/ticket-closure-validation';
import { sanitizeHtml } from '@/lib/sanitize-html';
import { formatDateTime, formatRelativeWithDate } from '@/lib/date-format';
import { SuggestedArticles } from '@/components/tickets/SuggestedArticles';
import { ExportTicketsDrawer } from '@/components/tickets/export-tickets-drawer';
import { useAuth } from '@/lib/auth';
import { useI18n, type TranslationKey } from '@/lib/i18n';

type Translator = ReturnType<typeof useI18n>['t'];

// ─── Types ────────────────────────────────────────────────────────────────────

interface TicketSummary {
  id: string;
  ticketNumber: number;
  subject: string;
  status: string;
  priority: string;
}

interface TicketDetail {
  id: string;
  ticketNumber: number;
  subject: string;
  description: string | null;
  status: string;
  priority: string;
  source: string;
  statusId: string | null;
  priorityId: string | null;
  sourceId: string | null;
  ticketType: string | null;
  ticketTypeId: string | null;
  createdAt: string;
  updatedAt: string;
  version: number;
  resolvedAt: string | null;
  closedAt: string | null;
  firstRespondedAt: string | null;
  dueBy: string | null;
  frDueBy: string | null;
  nrDueBy: string | null;
  frWarningAt: string | null;
  warningAt: string | null;
  isEscalated: boolean;
  escalationDueAt: string | null;
  deeplyEscalatedAt: string | null;
  dueByOverridden: boolean;
  customFields: Record<string, unknown> | null;
  requester: { id: string; firstName: string; lastName: string; email: string; avatar: string | null } | null;
  assignee: { id: string; firstName: string; lastName: string; email: string; avatar: string | null } | null;
  groupId: string | null;
  group: { id: string; name: string } | null;
  internalGroupId: string | null;
  internalGroup: { id: string; name: string } | null;
  company: { id: string; name: string } | null;
  slaPolicy: { id: string; name: string } | null;
  ticketState: { slaStoppedAt: string | null } | null;
  slaState?: SlaLifecycleState;
  parentTicket: TicketSummary | null;
  childTickets: TicketSummary[];
  tags: { tag: { id: string; name: string } }[];
  conversations: ConversationItem[];
  hasOlderConversations?: boolean;
  conversationCount?: number;
  attachments: { id: string; fileName: string; fileSize: number; contentType: string }[];
  ccEmails?: string[] | null;
  emailConfig?: { name: string; email: string } | null;
  activityLogs: ActivityItem[];
  ticketEvents: TicketEventItem[];
  watchers: { id: string; user: { id: string; firstName: string; lastName: string; email: string; avatar: string | null } }[];
}

interface ConversationItem {
  id: string;
  type: string;
  body: string;
  isPrivate: boolean;
  createdAt: string;
  editedAt?: string | null;
  user: { id: string; firstName: string; lastName: string; email: string; avatar: string | null } | null;
  attachments: { id: string; fileName: string; fileSize: number; contentType: string }[];
  thread?: { id: string; _count?: { messages: number } } | null;
  toEmails?: string[] | null;
  ccEmails?: string[] | null;
  bccEmails?: string[] | null;
  /** Emails of the agents a note was sent to ("Notified to"). */
  notifiedTo?: string[];
}

/** Json recipient columns arrive as unknown; keep only non-empty strings. */
function recipientList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
    : [];
}

function RecipientLine({ to, cc, bcc, notified }: { to?: string[]; cc?: string[]; bcc?: string[]; notified?: string[] }) {
  const { t } = useI18n();
  const rows: Array<[string, string[] | undefined]> = [
    [t('td.recipientTo'), to],
    [t('td.recipientCc'), cc],
    [t('td.recipientBcc'), bcc],
    [t('td.notifiedTo'), notified],
  ];
  const visible = rows.filter(([, list]) => list && list.length > 0);
  if (visible.length === 0) return null;
  return (
    <div className="mb-2 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-ink-muted">
      {visible.map(([label, list]) => (
        <span key={label} className="break-all">
          <span className="font-semibold">{label}:</span> {list!.join(', ')}
        </span>
      ))}
    </div>
  );
}

type TicketMutationResponse = Partial<Omit<TicketDetail, 'slaState'>> & {
  id: string;
  slaState?: SlaLifecycleState | null;
};

interface ActivityItem {
  id: string;
  action: string;
  changes: Record<string, { from: unknown; to: unknown }> | null;
  createdAt: string;
  user: { id: string; firstName: string; lastName: string } | null;
}

interface TicketEventItem {
  id: string;
  eventType: string;
  source: string;
  oldValue: Record<string, unknown> | null;
  newValue: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  actor: { id: string; firstName: string; lastName: string } | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const SKIP_IN_SIDEBAR = new Set([
  'requester', 'subject', 'description', 'source_info',
  // Requested removal from the ticket detail Properties panel — same set
  // hidden from the ticket list filter drawer (ticket-filters-drawer.tsx).
  'cf_final_status',                                                          // Final Status
  'cf_sdpl_pic',                                                               // SDPL PIC
  'cf_sdpl_observation',                                                      // SDPL Observation
  'cf_acceptable_criteria_in_detail_bullet_points',                           // Acceptable criteria (In detail- Bullet points)
  'cf_business_inconvenience_negative_impact',                                // Business inconvenience (negative impact)
  'cf_current_business_purposeflow_in_brief_pertaining_to_the_current_system', // Current: Business purpose/flow (in brief pertaining to the current system)
  'cf_business_vertical',                                                     // Business Vertical
  'cf_main_beneficiary',                                                      // Main Beneficiary
  'cf_attribute',                                                             // Attribute
  'cf_title_request',                                                        // Title (Request)
  'cf_system_name',                                                          // System
  'cf_cr_category',                                                          // CR Category
  'cf_current_functionality',                                                // Current Functionality
  'cf_cr_type',                                                              // CR Type
  'cf_bug_type',                                                             // Bug Type
]);

function isClosedStatusValue(field: TicketFieldRegistryField | undefined, value: unknown): boolean {
  const normalizedValue = String(value ?? '').trim().toLowerCase();
  if (normalizedValue === 'closed') return true;
  const option = field?.rawChoices?.find((candidate) => (
    candidate.value === String(value ?? '') || candidate.label.trim().toLowerCase() === normalizedValue
  ));
  return option?.label.trim().toLowerCase() === 'closed';
}

function isUuid(value: unknown): value is string {
  return typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}

function fieldErrorsFromApiError(error: unknown): Record<string, string> | null {
  if (!(error instanceof ApiError) || !error.data || typeof error.data !== 'object' || Array.isArray(error.data)) return null;
  const errors = (error.data as { errors?: unknown }).errors;
  if (!errors || typeof errors !== 'object' || Array.isArray(errors)) return null;
  const fieldErrors = Object.fromEntries(
    Object.entries(errors).filter((entry): entry is [string, string] => typeof entry[1] === 'string'),
  );
  return Object.keys(fieldErrors).length > 0 ? fieldErrors : null;
}

function formatAction(action: string, changes: Record<string, { from: unknown; to: unknown }> | null, t: Translator): string {
  if (action === 'ticket_created') return t('td.actionCreatedTicket');
  if ((action === 'ticket_updated' || action === 'ticket_status_changed') && changes) {
    const fields = Object.keys(changes).map((key) => changeFieldLabel(key, t)).join(', ');
    return t('td.actionUpdatedFields', { fields });
  }
  return action.replace(/_/g, ' ');
}

const EVENT_TAG_KEYS: Record<string, TranslationKey> = {
  ticket_created: 'ticketDetail.eventTicketCreated',
  status_changed: 'ticketDetail.eventStatusUpdated',
  ticket_status_changed: 'ticketDetail.eventStatusUpdated',
  assignment_changed: 'ticketDetail.eventAgentUpdated',
  sla_overridden: 'ticketDetail.eventSlaUpdated',
  ticket_merged: 'ticketDetail.eventTicketMerged',
  ticket_merge_received: 'ticketDetail.eventMergeReceived',
  ticket_split: 'ticketDetail.eventTicketSplit',
  ticket_updated: 'ticketDetail.eventUpdated',
  split_content_copied: 'ticketDetail.eventSplitContentCopied',
  reply: 'ticketDetail.eventReplySent',
  private_note: 'ticketDetail.eventNoteAdded',
  public_note: 'ticketDetail.eventNoteAdded',
  customer_reply: 'ticketDetail.eventCustomerReplied',
  ticket_deleted: 'ticketDetail.eventTicketDeleted',
};

function eventTagLabel(eventType: string, t: Translator): string {
  const key = EVENT_TAG_KEYS[eventType];
  return key ? t(key) : eventType.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

const CHANGE_FIELD_KEYS: Record<string, TranslationKey> = {
  status: 'td.fieldStatus',
  statusId: 'td.fieldStatus',
  priority: 'td.fieldPriority',
  priorityId: 'td.fieldPriority',
  source: 'td.fieldSource',
  sourceId: 'td.fieldSource',
  ticketTypeId: 'td.fieldType',
  groupId: 'td.fieldGroup',
  assigneeId: 'td.fieldAgent',
  companyId: 'td.fieldCompany',
  requesterId: 'td.fieldContact',
  subject: 'td.fieldSubject',
  description: 'td.fieldDescription',
  productId: 'td.fieldProduct',
  dueBy: 'td.fieldResolutionDue',
  frDueBy: 'td.fieldFirstResponseDue',
  tags: 'td.fieldTags',
  parentTicketId: 'td.fieldParentTicket',
};

function changeFieldLabel(key: string, t: Translator): string {
  const labelKey = CHANGE_FIELD_KEYS[key];
  return labelKey ? t(labelKey) : key;
}

function eventActionMatchesLog(eventType: string, action: string): boolean {
  const normalizedEventType = eventType === 'status_changed'
    ? 'ticket_status_changed'
    : eventType === 'private_note' || eventType === 'public_note'
      ? 'note_added'
      : eventType;
  return normalizedEventType === action;
}

interface ActivityEntry {
  id: string;
  createdAt: string;
  tag: string;
  description: string;
}

function describeEvent(event: TicketEventItem, t: Translator): string {
  const actor = event.actor ? `${event.actor.firstName} ${event.actor.lastName}` : t('td.system');
  const oldValue = event.oldValue ?? {};
  const newValue = event.newValue ?? {};
  if (event.eventType === 'ticket_created') {
    return t(newValue.parentTicketId ? 'td.eventCreatedLinked' : 'td.eventCreated', { actor });
  }
  if (event.eventType === 'ticket_split') {
    return t('td.eventSplit', { actor, ref: newValue.childTicketNumber ? ` #${newValue.childTicketNumber}` : '' });
  }
  if (event.eventType === 'ticket_merge_received') {
    return t('td.eventMergeReceived', { actor, ref: event.metadata && typeof event.metadata.sourceTicketNumber === 'number' ? ` (#${event.metadata.sourceTicketNumber})` : '' });
  }
  if (event.eventType === 'ticket_merged') {
    return t('td.eventMerged', { actor, ref: event.metadata && typeof event.metadata.targetTicketNumber === 'number' ? ` (#${event.metadata.targetTicketNumber})` : '' });
  }
  if (event.eventType === 'split_content_copied') {
    const conversations = typeof newValue.conversations === 'number' ? newValue.conversations : 0;
    const attachments = typeof newValue.attachments === 'number' ? newValue.attachments : 0;
    return t('td.eventSplitCopied', {
      actor,
      conversations: t(conversations === 1 ? 'td.conversationsOne' : 'td.conversationsMany', { count: conversations }),
      attachments: t(attachments === 1 ? 'td.attachmentsOne' : 'td.attachmentsMany', { count: attachments }),
    });
  }
  const changedFields = Object.keys(newValue).filter((key) => JSON.stringify(newValue[key]) !== JSON.stringify(oldValue[key]));
  if (changedFields.length === 0) return `${actor} ${eventTagLabel(event.eventType, t).toLowerCase()}`;
  const fields = changedFields.map((key) => changeFieldLabel(key, t)).join(', ');
  return t('td.eventUpdatedFields', { actor, fields });
}

export function buildActivityEntries(activityLogs: ActivityItem[], ticketEvents: TicketEventItem[], t: Translator): ActivityEntry[] {
  const matchedActivityIds = new Set<string>();
  const eventEntries = ticketEvents.map((event): ActivityEntry => {
    const matchingLog = activityLogs.find((log) => (
      !matchedActivityIds.has(log.id)
      && eventActionMatchesLog(event.eventType, log.action)
      && (!event.actor?.id || !log.user?.id || event.actor.id === log.user.id)
      && Math.abs(new Date(event.createdAt).getTime() - new Date(log.createdAt).getTime()) <= 2000
    ));
    if (matchingLog) matchedActivityIds.add(matchingLog.id);
    return {
      id: event.id,
      createdAt: event.createdAt,
      tag: eventTagLabel(event.eventType, t),
      description: describeEvent(event, t),
    };
  });
  const unmatchedLogs = activityLogs
    .filter((log) => !matchedActivityIds.has(log.id))
    .map((log): ActivityEntry => ({
      id: log.id,
      createdAt: log.createdAt,
      tag: eventTagLabel(log.action, t),
      description: `${log.user ? `${log.user.firstName} ${log.user.lastName}` : t('td.system')} ${formatAction(log.action, log.changes, t)}`,
    }));
  return [...eventEntries, ...unmatchedLogs]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
}

// SLA-07: an ISO instant → the local-wall-clock value a <input type="datetime-local">
// expects (YYYY-MM-DDTHH:mm). Subtracting the tz offset makes toISOString slice
// give local time rather than UTC. Returns '' for a null/absent due date.
function toDatetimeLocal(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

export type SlaOverrideMetric = 'first_response' | 'resolution';

/** Build the API body without turning omitted optional values into clears. */
export function buildSlaOverridePayload(input: {
  metric?: SlaOverrideMetric;
  dueAt?: string | null;
  expectedVersion: number;
  reason?: string;
  clear?: 'resolution' | 'escalation';
}): Record<string, unknown> {
  const payload: Record<string, unknown> = { expectedVersion: input.expectedVersion };
  if (input.clear === 'resolution') payload.dueBy = null;
  else if (input.clear === 'escalation') payload.clearEscalation = true;
  else if (input.metric && input.dueAt !== undefined) {
    payload[input.metric === 'first_response' ? 'frDueBy' : 'dueBy'] = input.dueAt;
  }
  if (input.reason?.trim()) payload.reason = input.reason.trim();
  return payload;
}

// SLA-07: relative due-date presets for the resolution override editor. Each
// resolves to end-of-day (23:59 local) on the target date, computed relative to
// `from` so "today" tracks the viewer's clock. Kept as data so the editor just
// maps over it — no per-button branching.
type SlaPreset = { key: string; labelKey: TranslationKey; resolve: (from: Date) => Date };

function endOfDay(d: Date): Date {
  const out = new Date(d);
  out.setHours(23, 59, 0, 0);
  return out;
}

const SLA_PRESETS: SlaPreset[] = [
  { key: 'today', labelKey: 'ticketDetail.presetToday', resolve: (from) => endOfDay(from) },
  { key: 'tomorrow', labelKey: 'ticketDetail.presetTomorrow', resolve: (from) => endOfDay(new Date(from.getTime() + 86400000)) },
  // End of the current week (Saturday), matching how the ticket list groups "this week".
  { key: 'this_week', labelKey: 'ticketDetail.presetThisWeek', resolve: (from) => endOfDay(new Date(from.getTime() + (6 - from.getDay()) * 86400000)) },
  { key: 'next_week', labelKey: 'ticketDetail.presetNextWeek', resolve: (from) => endOfDay(new Date(from.getTime() + (6 - from.getDay() + 7) * 86400000)) },
  // Last day of the current month.
  { key: 'this_month', labelKey: 'ticketDetail.presetThisMonth', resolve: (from) => endOfDay(new Date(from.getFullYear(), from.getMonth() + 1, 0)) },
];

// ─── Component ────────────────────────────────────────────────────────────────

export default function TicketDetailPage() {
  const { id: ticketId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { can, user } = useAuth();
  const { t, language } = useI18n();
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const [ticket, setTicket] = React.useState<TicketDetail | null>(null);
  const [slaState, setSlaState] = React.useState<SlaLifecycleState | null>(null);
  const [slaLoading, setSlaLoading] = React.useState(true);
  const [slaError, setSlaError] = React.useState<string | null>(null);
  const [slaMutationError, setSlaMutationError] = React.useState<string | null>(null);
  const [slaStale, setSlaStale] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [tab, setTab] = React.useState<'conversations' | 'activity'>('conversations');
  const [activityStale, setActivityStale] = React.useState(false);
  const [exportOpen, setExportOpen] = React.useState(false);
  const [watcherBusy, setWatcherBusy] = React.useState(false);
  const [descriptionOpen, setDescriptionOpen] = React.useState(false);
  const [descriptionDraft, setDescriptionDraft] = React.useState('');
  const [descriptionSaving, setDescriptionSaving] = React.useState(false);
  const [editSubjectDraft, setEditSubjectDraft] = React.useState('');
  const [editContactId, setEditContactId] = React.useState('');
  const [editContactOptions, setEditContactOptions] = React.useState<SearchableSelectOption[]>([]);
  const [editContactLoading, setEditContactLoading] = React.useState(false);
  const [editingConversationId, setEditingConversationId] = React.useState<string | null>(null);
  const [conversationEditDraft, setConversationEditDraft] = React.useState('');
  const [conversationEditSaving, setConversationEditSaving] = React.useState(false);
  const [threadConversationId, setThreadConversationId] = React.useState<string | null>(null);
  const [forwardConversation, setForwardConversation] = React.useState<ConversationItem | null>(null);
  const [olderConversations, setOlderConversations] = React.useState<ConversationItem[]>([]);
  const [hasMoreOlder, setHasMoreOlder] = React.useState(false);
  const [loadingOlder, setLoadingOlder] = React.useState(false);
  const [sidebarRowOpen, setSidebarRowOpen] = React.useState<Record<string, boolean>>({
    contact: false,
    kb: false,
    relationships: false,
    dates: false,
    jira: false,
  });
  const [propertiesCollapsed, setPropertiesCollapsed] = React.useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);
  const loadGeneration = React.useRef(0);

  function toggleSidebarRow(row: string) {
    setSidebarRowOpen((previous) => ({ ...previous, [row]: !previous[row] }));
  }

  function openSidebarRow(row: string) {
    setSidebarCollapsed(false);
    setSidebarRowOpen((previous) => Object.fromEntries(
      Object.keys(previous).map((key) => [key, key === row]),
    ) as Record<string, boolean>);
  }

  // Editable field state
  const [edits, setEdits] = React.useState<Record<string, unknown>>({});
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({});
  const [saving, setSaving] = React.useState(false);
  const [relationshipQuery, setRelationshipQuery] = React.useState('');
  const [relationshipResults, setRelationshipResults] = React.useState<TicketSummary[]>([]);
  const [relationshipBusy, setRelationshipBusy] = React.useState(false);
  const [splitSubject, setSplitSubject] = React.useState('');
  const [copySplitAttachments, setCopySplitAttachments] = React.useState(false);
  const [copySplitConversations, setCopySplitConversations] = React.useState(false);

  // Persisted field registry used by the properties editor.
  const [registryFields, setRegistryFields] = React.useState<TicketFieldRegistryField[]>([]);

  // SLA-07: admin-only manual override of the resolution due date. The editor
  // leads with relative presets (end-of-day); "Pick date & time" reveals the
  // native datetime-local input for a precise value. slaDueBy is the chosen
  // value as a datetime-local string.
  const [slaEditing, setSlaEditing] = React.useState(false);
  const [slaSaving, setSlaSaving] = React.useState(false);
  const [slaDueBy, setSlaDueBy] = React.useState('');
  const [slaMetric, setSlaMetric] = React.useState<SlaOverrideMetric>('resolution');
  const [slaReason, setSlaReason] = React.useState('');
  const [slaCustom, setSlaCustom] = React.useState(false);

  // Background attachment uploads from ticket creation flow
  const { getUploads, retryFailed, dismiss } = useAttachmentUploader();
  const pendingUploads = ticketId ? getUploads(ticketId) : [];
  const uploading = pendingUploads.filter((u) => u.status === 'uploading');
  const failed = pendingUploads.filter((u) => u.status === 'error');
  const uploadsSettled = pendingUploads.length > 0 && uploading.length === 0;

  React.useEffect(() => {
    const controller = new AbortController();
    loadTicket(controller.signal);
    void getTicketFields()
      .then((fields) => {
        if (!controller.signal.aborted) setRegistryFields(fields);
      })
      .catch((err) => {
        if (!(err instanceof Error && err.name === 'AbortError')) setError(err.message || t('ticketDetail.fieldsLoadError'));
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  // Refresh exactly once when an attachment batch settles. Failed uploads stay
  // visible so the user can retry; a successful batch is dismissed after the
  // authoritative refresh. The reply composer sends its email from the
  // uploader's onAllDone callback, preserving upload-before-send ordering.
  React.useEffect(() => {
    if (!ticketId || !uploadsSettled) return;
    loadTicket();
    if (failed.length === 0) dismiss(ticketId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadsSettled, ticketId, uploading.length, failed.length, pendingUploads.length]);

  React.useEffect(() => {
    if (tab !== 'activity' || !activityStale || !ticketId) return;
    setActivityStale(false);
    loadTicket();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityStale, tab, ticketId]);

  function applySlaResponse(value: unknown) {
    if (!ticketId) return;
    try {
      setSlaState(validateSlaLifecycleState(value, ticketId));
      setSlaError(null);
      setSlaMutationError(null);
      setSlaStale(false);
    } catch (err: unknown) {
      setSlaError(err instanceof Error ? err.message : t('td.invalidSlaState'));
      setSlaStale(true);
    }
  }

  function supersedeDetailLoad() {
    loadGeneration.current += 1;
    setLoading(false);
    setSlaLoading(false);
  }

  function applyTicketMutation(data?: TicketMutationResponse | null, redirectIfInaccessible = false) {
    if (!ticketId || !data || data.id !== ticketId) {
      // Backward compatibility for rolling deployments that return an empty
      // mutation body: retain the old authoritative detail reload path.
      loadTicket(undefined, redirectIfInaccessible);
      return;
    }
    const hasSlaState = Object.prototype.hasOwnProperty.call(data, 'slaState');
    const needsSlaReload = !hasSlaState && slaState !== null;
    const { slaState: nextSlaState, ...ticketPatch } = data;

    // A settlement/detail refresh may already be in flight. The mutation
    // response is newer and authoritative for this write, so invalidate that
    // older load before applying the local patch.
    supersedeDetailLoad();
    setActivityStale(true);
    setTicket((current) => {
      if (!current || current.id !== data.id) return current;
      return {
        ...current,
        ...ticketPatch,
        ...(hasSlaState ? { slaState: nextSlaState ?? undefined } : {}),
      };
    });
    if (redirectIfInaccessible) {
      // A complete mutation response is not proof that the ticket remains
      // visible under the caller's scope. Re-read it so the backend can return
      // 403/404 after a group move and loadTicket can send the user to the list.
      loadTicket(undefined, true);
    } else if (hasSlaState) {
      if (nextSlaState) {
        applySlaResponse(nextSlaState);
      } else {
        setSlaState(null);
        setSlaError(null);
        setSlaStale(false);
      }
    } else if (needsSlaReload && tab !== 'activity') {
      loadTicket(undefined, false);
    }
    setEdits({});
    setFieldErrors({});
    setError(null);
  }

  function replaceConversation(updated?: ConversationItem | null) {
    if (!updated?.id) {
      loadTicket();
      return;
    }
    supersedeDetailLoad();
    setActivityStale(true);
    setTicket((current) => current
      ? { ...current, conversations: current.conversations.map((conversation) => conversation.id === updated.id ? updated : conversation) }
      : current);
    setOlderConversations((current) => current.map((conversation) => conversation.id === updated.id ? updated : conversation));
  }

  function appendConversation(conversation: ConversationItem) {
    supersedeDetailLoad();
    setActivityStale(true);
    setTicket((current) => {
      if (!current || current.conversations.some((entry) => entry.id === conversation.id)) return current;
      const conversationCount = current.conversationCount ?? current.conversations.length;
      return {
        ...current,
        conversations: [...current.conversations, conversation],
        conversationCount: conversationCount + 1,
      };
    });
  }

  function handleReplySent({ conversation, attachmentsPending, slaState: nextSlaState }: ReplySentEvent) {
    if (attachmentsPending) return;
    appendConversation(conversation);
    if (nextSlaState !== undefined && nextSlaState !== null) applySlaResponse(nextSlaState);
  }

  function loadTicket(signal?: AbortSignal, redirectIfInaccessible = false) {
    if (!ticketId) return;
    const generation = ++loadGeneration.current;
    setLoading(true);
    setSlaLoading(true);
    api.get<{ data: TicketDetail }>(`/tickets/${ticketId}`, { signal })
      .then(async ({ data }) => {
        if (signal?.aborted || generation !== loadGeneration.current) return;

        setTicket(data);
        setActivityStale(false);
        setEdits({});
        setFieldErrors({});
        setError(null);
        setOlderConversations([]);
        setHasMoreOlder(!!data.hasOlderConversations);

        // PERF-02: current backends include authoritative SLA state in the
        // detail response. Fall back for older deployments or a best-effort
        // SLA read failure so rolling deploys and existing error handling work.
        if (data.slaState !== undefined && data.slaState !== null) {
          applySlaResponse(data.slaState);
          return;
        }

        try {
          const slaResult = await api.get<{ data: unknown }>(`/tickets/${ticketId}/sla`, { signal });
          if (signal?.aborted || generation !== loadGeneration.current) return;
          applySlaResponse(slaResult.data);
        } catch (err: unknown) {
          if (err instanceof Error && err.name === 'AbortError') return;
          setSlaError(err instanceof Error ? err.message : t('td.slaRefreshError'));
          setSlaStale(true);
        }
      })
      .catch((err: unknown) => {
        if (signal?.aborted || generation !== loadGeneration.current) return;
        if (redirectIfInaccessible && err instanceof ApiError && (err.status === 403 || err.status === 404)) {
          navigate('/tickets', { replace: true });
          return;
        }
        setError(err instanceof Error ? err.message : t('ticketDetail.loadErrorTitle'));
      })
      .finally(() => {
        if (!signal?.aborted && generation === loadGeneration.current) {
          setLoading(false);
          setSlaLoading(false);
        }
      });
  }

  // Get field value from ticket columns or persisted custom-field keys.
  function getFieldValue(field: Pick<TicketFieldRegistryField, 'fieldKey' | 'sourceType'>): unknown {
    if (!ticket) return '';
    if (edits[field.fieldKey] !== undefined) return edits[field.fieldKey];
    switch (field.sourceType) {
      case 'default_status': return ticket.statusId || ticket.status || '';
      case 'default_priority': return ticket.priorityId || ticket.priority || '';
      case 'default_source': return ticket.sourceId || '';
      case 'default_ticket_type': return ticket.ticketTypeId || ticket.ticketType || ticket.customFields?.ticket_type || '';
      case 'default_group': return ticket.group?.id ?? ticket.groupId ?? ticket.group?.name ?? '';
      case 'default_internal_group': return ticket.internalGroup?.id ?? ticket.internalGroupId ?? ticket.internalGroup?.name ?? '';
      case 'default_agent':
      case 'default_internal_agent': return ticket.assignee?.id || '';
      case 'default_company': return ticket.company?.id || '';
      default: return ticket.customFields?.[field.fieldKey] ?? '';
    }
  }

  function setFieldEdit(name: string, value: unknown) {
    setEdits((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => {
      if (name === 'status') return {};
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }

  async function handleUpdate() {
    if (!ticket || Object.keys(edits).length === 0) return;

    const nextFieldValues = Object.fromEntries(
      registryFields.map((field) => [field.fieldKey, getFieldValue(field)]),
    );
    const statusField = registryFields.find((field) => field.sourceType === 'default_status');
    const isClosing = ticket.status !== 'closed' && isClosedStatusValue(statusField, nextFieldValues.status);
    if (isClosing) {
      const closureErrors = validateTicketClosureFields({
        status: nextFieldValues.status,
        priority: nextFieldValues.priority,
        ticketType: nextFieldValues.ticket_type,
        group: nextFieldValues.group,
        customFields: {
          ...(ticket.customFields || {}),
          ...Object.fromEntries(
            TICKET_CLOSURE_REQUIRED_FIELDS
              .filter((field) => field.source === 'custom' && Object.prototype.hasOwnProperty.call(nextFieldValues, field.fieldKey))
              .map((field) => [field.fieldKey, nextFieldValues[field.fieldKey]]),
          ),
        },
      });
      if (Object.keys(closureErrors).length > 0) {
        setFieldErrors(closureErrors);
        setError(null);
        return;
      }
    }

    setFieldErrors({});
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {};
      const customFields: Record<string, unknown> = { ...(ticket.customFields || {}) };
      const fieldsByKey = new Map(registryFields.map((field) => [field.fieldKey, field]));

      for (const [key, value] of Object.entries(edits)) {
        switch (fieldsByKey.get(key)?.sourceType) {
          case 'default_requester': payload.requesterId = value; break;
          case 'default_description': payload.description = value; break;
          case 'default_status': isUuid(value) ? payload.statusId = value : payload.status = value; break;
          case 'default_priority': isUuid(value) ? payload.priorityId = value : payload.priority = value; break;
          case 'default_source': isUuid(value) ? payload.sourceId = value : payload.source = value; break;
          case 'default_ticket_type': isUuid(value) ? payload.ticketTypeId = value : payload.ticketType = value; break;
          case 'default_group': payload.groupId = value || null; break;
          case 'default_internal_group': payload.internalGroupId = value || null; break;
          case 'default_agent':
          case 'default_internal_agent': payload.assigneeId = value || null; break;
          case 'default_company': payload.companyId = value || null; break;
          default: customFields[key] = value;
        }
      }

      if (Object.keys(customFields).length > 0) payload.customFields = customFields;

      const previousGroupId = ticket.group?.id ?? ticket.groupId ?? null;
      const { data } = await api.put<{ data: TicketMutationResponse }>(`/tickets/${ticketId}`, payload);
      const groupChanged = data.id === ticket.id
        && Object.prototype.hasOwnProperty.call(data, 'groupId')
        && (data.groupId ?? null) !== previousGroupId;
      applyTicketMutation(data, groupChanged);
      toast({ variant: 'success', title: t('ticketDetail.updateSuccess') });
    } catch (err: any) {
      const backendFieldErrors = fieldErrorsFromApiError(err);
      if (backendFieldErrors) {
        setFieldErrors(backendFieldErrors);
        setError(null);
        return;
      }
      const message = err instanceof Error ? err.message : (err?.message || t('ticketDetail.updateError'));
      setError(message);
      toast({ variant: 'error', title: t('ticketDetail.updateError'), description: message });
    } finally {
      setSaving(false);
    }
  }

  function openDescriptionEditor() {
    if (!ticket || !can('edit')) return;
    setDescriptionDraft(ticket.description ?? '');
    setEditSubjectDraft(ticket.subject);
    setEditContactId(ticket.requester?.id ?? '');
    setEditContactOptions(ticket.requester ? [{
      value: ticket.requester.id,
      label: `${ticket.requester.firstName} ${ticket.requester.lastName}`,
      sublabel: ticket.requester.email,
    }] : []);
    setDescriptionOpen(true);
  }

  function handleEditContactSearch(query: string) {
    if (query.length < 2) return;
    setEditContactLoading(true);
    api
      .get<{ data: SearchableSelectOption[] }>('/contacts/search', { params: { q: query } })
      .then((res) => setEditContactOptions(res.data))
      .finally(() => setEditContactLoading(false));
  }

  async function saveDescription() {
    if (!ticketId || !can('edit')) return;
    setDescriptionSaving(true);
    try {
      const payload: Record<string, unknown> = {
        description: descriptionDraft,
        subject: editSubjectDraft,
      };
      if (editContactId) payload.requesterId = editContactId;
      const { data } = await api.put<{ data: TicketMutationResponse }>(`/tickets/${ticketId}`, payload);
      applyTicketMutation(data);
      toast({ variant: 'success', title: t('td.descriptionUpdated') });
      setDescriptionOpen(false);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t('td.descriptionUpdateError');
      setError(message);
      toast({ variant: 'error', title: t('td.descriptionUpdateFailed'), description: message });
    } finally {
      setDescriptionSaving(false);
    }
  }

  function openConversationEditor(conv: ConversationItem) {
    setEditingConversationId(conv.id);
    setConversationEditDraft(conv.body);
  }

  function cancelConversationEdit() {
    setEditingConversationId(null);
    setConversationEditDraft('');
  }

  async function saveConversationEdit(conversationId: string) {
    if (!ticketId) return;
    setConversationEditSaving(true);
    try {
      const { data } = await api.patch<{ data: ConversationItem }>(`/tickets/${ticketId}/conversations/${conversationId}`, { body: conversationEditDraft });
      replaceConversation(data);
      toast({ variant: 'success', title: t('td.entryUpdated') });
      setEditingConversationId(null);
      setConversationEditDraft('');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t('td.entryUpdateError');
      toast({ variant: 'error', title: t('td.updateFailed'), description: message });
    } finally {
      setConversationEditSaving(false);
    }
  }

  /**
   * Opens the thread drawer for a conversation entry. Does NOT call any API
   * — merely looking does not create a thread. The drawer itself lazily
   * creates the ConversationThread row only when the agent actually sends
   * the first message (see ThreadDrawer.sendMessage → addThreadMessage).
   */
  function openThread(conversationId: string) {
    setThreadConversationId(conversationId);
  }

  function openForwardDrawer(conv: ConversationItem) {
    setForwardConversation(conv);
  }

  async function loadOlderConversations() {
    if (!ticketId || !ticket || loadingOlder) return;
    const oldestVisible = (olderConversations[0] ?? ticket.conversations[0])?.id;
    if (!oldestVisible) return;
    setLoadingOlder(true);
    try {
      const res = await api.get<{ data: ConversationItem[]; meta: { hasOlder: boolean } }>(
        `/tickets/${ticketId}/conversations`,
        { params: { cursor: oldestVisible, limit: 25 } },
      );
      setOlderConversations((prev) => [...res.data, ...prev]);
      setHasMoreOlder(res.meta.hasOlder);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t('td.loadOlderError');
      toast({ variant: 'error', title: t('td.loadFailed'), description: message });
    } finally {
      setLoadingOlder(false);
    }
  }

  function obligationForMetric(metric: SlaOverrideMetric): SlaMetricObligation | null {
    const obligations = slaState?.obligations.filter((item) => item.metric === metric) ?? [];
    return obligations.length > 0 ? obligations[obligations.length - 1] : null;
  }

  function metricDueValue(metric: SlaOverrideMetric): string | null {
    const obligation = obligationForMetric(metric);
    if (obligation?.dueAt) return obligation.dueAt;
    if (metric === 'first_response') return ticket?.frDueBy ?? null;
    return ticket?.dueBy ?? null;
  }

  // SLA-07: open the inline editor for one independently enforced metric.
  function openSlaEditor(metric: SlaOverrideMetric = 'resolution') {
    if (!ticket) return;
    setSlaMetric(metric);
    setSlaDueBy(toDatetimeLocal(metricDueValue(metric)));
    setSlaReason('');
    setSlaMutationError(null);
    setSlaCustom(false);
    setSlaEditing(true);
  }

  // Apply a relative preset and submit immediately. The server remains the
  // authority for the resulting metric state and thresholds.
  function applySlaPreset(preset: SlaPreset) {
    const next = toDatetimeLocal(preset.resolve(new Date()).toISOString());
    setSlaDueBy(next);
    void handleSlaOverride(next);
  }

  async function handleSlaOverride(value?: string) {
    if (!ticketId || !ticket) return;
    const chosen = value ?? slaDueBy;
    if (!chosen) {
      toast({ variant: 'error', title: t('ticketDetail.pickDateTitle'), description: t('ticketDetail.pickDateDescription') });
      return;
    }
    if (slaReason.trim().length > 500) {
      toast({ variant: 'error', title: t('td.slaUpdateFailed'), description: t('td.reasonTooLong') });
      return;
    }
    const deadline = new Date(chosen);
    if (!Number.isFinite(deadline.getTime())) {
      toast({ variant: 'error', title: t('td.slaUpdateFailed'), description: t('td.invalidDateTime') });
      return;
    }
    setSlaSaving(true);
    setSlaMutationError(null);
    try {
      const { data } = await api.patch<{ data: TicketMutationResponse }>(`/tickets/${ticketId}/sla-override`, buildSlaOverridePayload({
        metric: slaMetric,
        dueAt: deadline.toISOString(),
        expectedVersion: ticket.version,
        reason: slaReason,
      }));
      applyTicketMutation(data);
      toast({ variant: 'success', title: t('td.slaDeadlineUpdated') });
      setSlaEditing(false);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t('ticketDetail.slaUpdateError');
      setSlaStale(err instanceof ApiError && err.status === 409);
      setSlaMutationError(err instanceof ApiError && err.status === 409 ? t('td.slaConflict') : message);
      toast({ variant: 'error', title: t('td.slaUpdateFailed'), description: message });
    } finally {
      setSlaSaving(false);
    }
  }

  async function clearSlaTarget(target: 'resolution' | 'escalation') {
    if (!ticketId || !ticket) return;
    if (slaReason.trim().length > 500) {
      toast({ variant: 'error', title: t('td.slaClearFailed'), description: t('td.reasonTooLong') });
      return;
    }
    setSlaSaving(true);
    setSlaMutationError(null);
    try {
      const { data } = await api.patch<{ data: TicketMutationResponse }>(`/tickets/${ticketId}/sla-override`, buildSlaOverridePayload({
        clear: target,
        expectedVersion: ticket.version,
        reason: slaReason,
      }));
      applyTicketMutation(data);
      toast({ variant: 'success', title: t('td.slaTargetCleared') });
      setSlaEditing(false);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t('td.slaClearError');
      setSlaStale(err instanceof ApiError && err.status === 409);
      setSlaMutationError(err instanceof ApiError && err.status === 409 ? t('td.slaConflict') : message);
      toast({ variant: 'error', title: t('td.slaClearFailed'), description: message });
    } finally {
      setSlaSaving(false);
    }
  }

  async function searchRelationshipTickets() {
    const query = relationshipQuery.trim();
    if (query.length < 2) return;
    setRelationshipBusy(true);
    try {
      const response = await api.get<{ data: { tickets: TicketSummary[] } }>('/tickets/search', {
        params: { q: query, limit: 10 },
      });
      setRelationshipResults(response.data.tickets.filter((candidate) => candidate.id !== ticketId));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('ticketDetail.searchError'));
    } finally {
      setRelationshipBusy(false);
    }
  }

  async function setParentTicket(parentTicketId: string | null) {
    if (!ticketId) return;
    setRelationshipBusy(true);
    try {
      await api.put(`/tickets/${ticketId}`, { parentTicketId });
      setRelationshipResults([]);
      await loadTicket();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('ticketDetail.parentUpdateError'));
    } finally {
      setRelationshipBusy(false);
    }
  }

  async function mergeInto(target: TicketSummary) {
    if (!ticketId || !ticket) return;
    const confirmed = await confirm({
      title: t('ticketDetail.confirmMergeTitle', { source: ticket.ticketNumber, target: target.ticketNumber }),
      description: t('ticketDetail.confirmMergeDescription'),
      confirmLabel: t('ticketDetail.confirmMergeLabel'),
    });
    if (!confirmed) return;
    setRelationshipBusy(true);
    try {
      await api.post(`/tickets/${ticketId}/merge`, { targetTicketId: target.id });
      toast({ variant: 'success', title: t('ticketDetail.mergeSuccess', { number: target.ticketNumber }) });
      navigate(`/tickets/${target.id}`);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t('ticketDetail.mergeError');
      setError(message);
      toast({ variant: 'error', title: t('ticketDetail.mergeError'), description: message });
      setRelationshipBusy(false);
    }
  }

  async function splitTicket() {
    if (!ticketId || !splitSubject.trim()) return;
    setRelationshipBusy(true);
    try {
      const response = await api.post<{ data: { id: string } }>(`/tickets/${ticketId}/split`, {
        subject: splitSubject.trim(),
        copyAttachments: copySplitAttachments,
        copyConversations: copySplitConversations,
      });
      toast({ variant: 'success', title: t('ticketDetail.splitSuccess') });
      navigate(`/tickets/${response.data.id}`);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t('ticketDetail.splitError');
      setError(message);
      toast({ variant: 'error', title: t('ticketDetail.splitError'), description: message });
      setRelationshipBusy(false);
    }
  }

  async function downloadAttachment(id: string, fileName: string) {
    try {
      // Fetch only the authorized signed URL through the API. Navigating to
      // S3 directly avoids trying to expose the cross-origin object response
      // to credentialed JavaScript, which browsers reject as CORS.
      const response = await api.get<{ data: { url: string } }>(
        `/attachments/${encodeURIComponent(id)}`,
        { params: { meta: true, download: true } },
      );
      const link = document.createElement('a');
      link.href = response.data.url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t('ticketDetail.downloadError');
      setError(message);
      toast({ variant: 'error', title: t('ticketDetail.downloadError'), description: message });
    }
  }

  async function toggleWatcher() {
    if (!ticketId || !user || !ticket) return;
    const isWatching = ticket.watchers.some((watcher) => watcher.user.id === user.id);
    setWatcherBusy(true);
    setError(null);
    try {
      if (isWatching) await api.delete(`/tickets/${ticketId}/watchers/me`);
      else await api.post(`/tickets/${ticketId}/watchers`);
      toast({ variant: 'success', title: isWatching ? t('ticketDetail.unfollowSuccess') : t('ticketDetail.followSuccess') });
      loadTicket();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t('ticketDetail.watcherError');
      setError(message);
      toast({ variant: 'error', title: t('ticketDetail.watcherError'), description: message });
    } finally {
      setWatcherBusy(false);
    }
  }

  // ─── Render ─────────────────────────────────────────────────────────────────

  // Mirrors the real two-column detail layout so the content swap does not
  // reflow the page (§2.21).
  if (loading)
    return (
      <div className="space-y-4" aria-busy="true">
        <div className="flex items-center gap-3">
          <Skeleton className="h-8 w-20" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-[22px] w-48 rounded-full" />
            <Skeleton className="h-5 w-2/5" />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 space-y-4">
            <Card><CardContent className="pt-6"><SkeletonText lines={4} /></CardContent></Card>
            <Card><CardContent className="pt-6"><SkeletonText lines={3} /></CardContent></Card>
          </div>
          <div className="space-y-4">
            <Card><CardContent className="space-y-3 pt-6"><Skeleton className="h-3 w-20" /><Skeleton className="h-10 w-full" /></CardContent></Card>
            <Card><CardContent className="space-y-3 pt-6"><Skeleton className="h-3 w-24" /><SkeletonText lines={4} /></CardContent></Card>
          </div>
        </div>
      </div>
    );
  if (error || !ticket)
    return (
      <ErrorState
        title={t('ticketDetail.loadErrorTitle')}
        message={error || t('ticketDetail.notFound')}
        onRetry={() => loadTicket()}
      />
    );

  const sidebarFields: DynamicTicketField[] = registryFields
    .filter((field) => (
      field.isActive !== false
      && !SKIP_IN_SIDEBAR.has(field.fieldKey)
      && !['default_agent', 'default_internal_group', 'default_internal_agent'].includes(field.sourceType)
      && field.sourceType !== 'default_company'
    ))
    .map((field) => ({
      fieldKey: field.fieldKey,
      label: field.label,
      description: field.description,
      labelForCustomers: field.labelForCustomers,
      sourceType: field.sourceType,
      rendererType: field.rendererType,
      requiredForAgents: field.requiredForAgents,
      requiredForCustomers: field.requiredForCustomers,
      requiredForClosure: field.requiredForClosure,
      displayedToCustomers: field.displayedToCustomers,
      customersCanEdit: field.customersCanEdit,
      isDefault: field.isDefault,
      rawChoices: field.rawChoices,
      choices: field.rawChoices,
      dependsOnFieldKey: field.dependsOnFieldKey,
      enabled: true,
      requiredOverride: null,
      visibleToCustomers: true,
    }));
  const fieldValues = Object.fromEntries(
    registryFields.map((field) => [field.fieldKey, getFieldValue(field)]),
  );
  const activityEntries = buildActivityEntries(ticket.activityLogs, ticket.ticketEvents, t);
  const displayedConversations = [...olderConversations, ...ticket.conversations];
  const totalConversationCount = ticket.conversationCount ?? displayedConversations.length;
  const hiddenOlderCount = Math.max(totalConversationCount - displayedConversations.length, 0);

  return (
    <div className="flex h-full min-h-0 flex-col bg-surface-page">
      {/* Fixed ticket header; only the workspace below scrolls. */}
      <div className="flex shrink-0 items-center gap-3 border-b border-line bg-surface-page px-4 py-3 sm:px-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/tickets')}>
          ← {t('ticketDetail.back')}
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm text-ink-subtle">#{ticket.ticketNumber}</span>
            <StatusPill value={ticket.status} />
            <PriorityPill value={ticket.priority} />
          </div>
          <h1 className="mt-1 text-xl font-semibold text-ink">{ticket.subject}</h1>
        </div>
        {user && user.role !== 'customer' && (
          <Button
            variant="outline"
            size="sm"
            aria-pressed={ticket.watchers.some((watcher) => watcher.user.id === user.id)}
            disabled={watcherBusy}
            onClick={() => void toggleWatcher()}
          >
            {ticket.watchers.some((watcher) => watcher.user.id === user.id) ? t('ticketDetail.following') : t('ticketDetail.follow')}
          </Button>
        )}
        {can('export') && (
          <Button variant="outline" size="sm" onClick={() => setExportOpen(true)}>
            {t('tickets.export')}
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={() => setTab(tab === 'activity' ? 'conversations' : 'activity')}
        >
          {tab === 'activity' ? t('ticketDetail.hideActivities') : t('ticketDetail.activitiesButton')}
        </Button>
      </div>

      {error && (
        <div className="shrink-0 px-4 pt-3 sm:px-6">
          <ErrorState
            message={error}
            onRetry={() => {
              setError(null);
              loadTicket();
            }}
          />
        </div>
      )}

      {/* Fixed side panes frame the independently scrolling ticket content. */}
      <div className="flex min-h-0 flex-1 overflow-hidden">
        {/* Center: conversations and activity */}
        <div className="min-h-0 min-w-0 flex-1 overflow-y-auto px-4 pb-6 pt-4 sm:px-6">
          <div className="space-y-4">
          {/* Description */}
          <Card className="overflow-hidden">
            <CardContent className="pt-4">
              <div className="mb-3 space-y-1.5">
                <p className="text-sm text-ink-muted">
                  <span className="font-semibold text-brand-700">
                    {ticket.requester ? `${ticket.requester.firstName} ${ticket.requester.lastName}` : t('td.system')}
                  </span>{' '}
                  {t('td.reportedVia', { source: ticket.source.replace(/_/g, ' ') })}
                  <span className="ml-1 italic text-ink-subtle">
                    {'\u2022'} {formatRelativeWithDate(ticket.createdAt, user?.timezone)}
                  </span>
                </p>
                <RecipientLine
                  to={ticket.emailConfig ? [`"${ticket.emailConfig.name}" <${ticket.emailConfig.email}>`] : undefined}
                  cc={recipientList(ticket.ccEmails)}
                />
              </div>
              <div className="flex items-start gap-3">
                <div
                  className={cn('min-w-0 flex-1', can('edit') && 'cursor-pointer')}
                  onClick={can('edit') ? openDescriptionEditor : undefined}
                >
                  {ticket.description ? (
                    <div
                      className="ticket-rich-content prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(ticket.description) }}
                    />
                  ) : (
                    <p className="text-sm italic text-ink-subtle">{t('td.noDescription')}</p>
                  )}
                </div>
                {can('edit') && (
                  <button
                    type="button"
                    onClick={openDescriptionEditor}
                    aria-label={t('td.editDescriptionAria')}
                    className="mt-0.5 shrink-0 rounded p-1.5 text-ink-subtle hover:bg-surface-hover hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                  >
                    <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Attachments with preview */}
          {ticket.attachments && ticket.attachments.length > 0 && (
            <Card>
              <CardContent className="pt-4">
                <p className="mb-3 text-xs font-medium text-ink-subtle">
                  {t('ticketDetail.attachmentsCount', { count: ticket.attachments.length })}
                </p>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {ticket.attachments.map((att) => {
                    const isImage = att.contentType.startsWith('image/');
                    const isPdf = att.contentType === 'application/pdf';
                    const ext = att.fileName.split('.').pop()?.toUpperCase() || 'FILE';
                    return (
                      <button
                        key={att.id}
                        type="button"
                        onClick={() => void downloadAttachment(att.id, att.fileName)}
                        aria-label={t('td.downloadFile', { fileName: att.fileName })}
                        title={t('td.download')}
                        className="group flex flex-col items-center gap-1.5 rounded-lg border border-line bg-surface-hover p-3 text-center transition hover:border-brand-300 hover:bg-brand-50"
                      >
                        {isImage ? (
                          <AttachmentPreview
                            attachmentId={att.id}
                            fileName={att.fileName}
                            className="h-20 w-full rounded object-cover"
                          />
                        ) : (
                          <div className="flex h-20 w-full items-center justify-center rounded bg-surface-subtle text-2xl font-bold text-ink-subtle">
                            {isPdf ? 'PDF' : ext}
                          </div>
                        )}
                        <span className="w-full truncate text-xs font-medium text-ink-muted group-hover:text-brand-600">
                          {att.fileName}
                        </span>
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-brand-700">
                          <Download className="h-3 w-3" aria-hidden="true" />
                          {t('td.download')}
                        </span>
                        <span className="text-xs text-ink-subtle">
                          {att.fileSize < 1024 * 1024
                            ? `${(att.fileSize / 1024).toFixed(0)} KB`
                            : `${(att.fileSize / (1024 * 1024)).toFixed(1)} MB`}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Tab switcher. TICKET-12: system-type conversations (e.g. "Status
              changed to Resolved") count toward and render in the
              conversations tab instead of being silently dropped. */}
          <Tabs
            aria-label={t('ticketDetail.threadAria')}
            items={[
              { value: 'conversations', label: t('ticketDetail.conversationsTabLabel'), count: totalConversationCount },
              { value: 'activity', label: t('ticketDetail.activitiesTabLabel'), count: activityEntries.length },
            ]}
            value={tab}
            onChange={(value) => setTab(value as 'conversations' | 'activity')}
          />

          {/* Conversations */}
          {tab === 'conversations' && (
            <div className="space-y-3">
              {displayedConversations.length === 0 ? (
                <EmptyState
                  title={t('ticketDetail.noRepliesYet')}
                  description={t('ticketDetail.noRepliesDescription')}
                />
              ) : (
                <>
                  {hasMoreOlder && (
                    <div className="flex justify-center py-1">
                      <button
                        type="button"
                        onClick={() => void loadOlderConversations()}
                        disabled={loadingOlder}
                        className="flex items-center gap-1.5 rounded-full border border-line bg-surface px-3 py-1.5 text-xs font-medium text-brand-700 shadow-sm hover:bg-brand-50 disabled:opacity-60"
                      >
                        {loadingOlder
                          ? t('common.loading')
                          : t(hiddenOlderCount === 1 ? 'td.loadOlder' : 'td.loadOlderMany', { count: hiddenOlderCount })}
                      </button>
                    </div>
                  )}
                  {displayedConversations.map((conv) =>
                  conv.type === 'system' ? (
                    // TICKET-12: system events render as a compact muted
                    // row (matching the Activity tab's visual language)
                    // rather than a full reply Card, since their bodies are
                    // machine-generated one-liners, not agent/customer prose.
                    <div
                      key={conv.id}
                      className="flex items-start gap-3 rounded-md bg-surface-hover px-3 py-2 text-sm text-ink-subtle"
                    >
                      <span className="mt-0.5 text-ink-subtle">⚙</span>
                      <div
                        className="ticket-rich-content min-w-0 max-w-full flex-1"
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(conv.body) }}
                      />
                      <span className="text-xs text-ink-subtle">
                        {formatDateTime(conv.createdAt, user?.timezone)}
                      </span>
                    </div>
                  ) : (
                    <Card
                      key={conv.id}
                      className={conv.isPrivate ? 'group border-warning-500/30 bg-warning-50' : 'group'}
                    >
                      <CardContent className="pt-4">
                        <div className="mb-2 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Avatar
                              name={conv.user ? `${conv.user.firstName} ${conv.user.lastName}` : t('td.system')}
                              id={conv.user?.id}
                              size="sm"
                            />
                            <span className="text-sm font-medium">
                              {conv.user?.firstName} {conv.user?.lastName}
                            </span>
                            {conv.isPrivate && (
                              <Badge variant="warning">{t('ticketDetail.privateNote')}</Badge>
                            )}
                            {conv.type === 'forward' && (
                              <Badge className="bg-status-product-background text-status-product-text">{t('ticketDetail.forward')}</Badge>
                            )}
                            {conv.editedAt && (
                              <span className="text-xs text-ink-subtle">{t('td.edited')}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs italic text-ink-subtle">
                              {formatRelativeWithDate(conv.createdAt, user?.timezone)}
                            </span>
                            <ConversationActions
                              canEdit={can('conversation_edit') && conv.type !== 'forward'}
                              canThread={can('thread_start')}
                              canForward={can('forward')}
                              onEdit={() => openConversationEditor(conv)}
                              onThread={() => openThread(conv.id)}
                              onForward={() => openForwardDrawer(conv)}
                            />
                          </div>
                        </div>
                        <RecipientLine
                          to={recipientList(conv.toEmails)}
                          cc={recipientList(conv.ccEmails)}
                          bcc={recipientList(conv.bccEmails)}
                          notified={conv.notifiedTo}
                        />
                        {editingConversationId === conv.id ? (
                          <div className="space-y-2">
                            <RichTextEditor
                              value={conversationEditDraft}
                              onChange={setConversationEditDraft}
                              minHeight="90px"
                              disabled={conversationEditSaving}
                            />
                            <div className="flex justify-end gap-2">
                              <Button variant="ghost" size="sm" disabled={conversationEditSaving} onClick={cancelConversationEdit}>
                                {t('common.cancel')}
                              </Button>
                              <Button size="sm" disabled={conversationEditSaving} onClick={() => void saveConversationEdit(conv.id)}>
                                {conversationEditSaving ? t('common.saving') : t('common.save')}
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div
                            className="ticket-rich-content prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{ __html: sanitizeHtml(conv.body) }}
                          />
                        )}
                        {conv.thread && (conv.thread._count?.messages ?? 0) > 0 && (
                          <button
                            type="button"
                            onClick={() => openThread(conv.id)}
                            className="mt-2 flex items-center gap-1.5 text-xs font-medium text-brand-700 hover:underline"
                          >
                            <MessageSquare className="h-3.5 w-3.5" aria-hidden="true" />
                            {t('td.privateThread')}
                          </button>
                        )}
                        {conv.attachments.length > 0 && (
                          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
                            {conv.attachments.map((att) => {
                              const isImage = att.contentType?.startsWith('image/');
                              const ext = att.fileName.split('.').pop()?.toUpperCase() || 'FILE';
                              return (
                                <button
                                  key={att.id}
                                  type="button"
                                  onClick={() => void downloadAttachment(att.id, att.fileName)}
                                  aria-label={t('td.downloadFile', { fileName: att.fileName })}
                                  title={t('td.download')}
                                  className="group flex flex-col items-center gap-1 rounded-lg border border-line bg-surface p-2 text-center transition hover:border-brand-300 hover:bg-brand-50"
                                >
                                  {isImage ? (
                                    <AttachmentPreview
                                      attachmentId={att.id}
                                      fileName={att.fileName}
                                      className="h-16 w-full rounded object-cover"
                                    />
                                  ) : (
                                    <div className="flex h-16 w-full items-center justify-center rounded bg-surface-subtle text-lg font-bold text-ink-subtle">
                                      {ext}
                                    </div>
                                  )}
                                  <span className="w-full truncate text-xs font-medium text-ink-muted group-hover:text-brand-600">
                                    {att.fileName}
                                  </span>
                                  <span className="inline-flex items-center gap-1 text-xs font-medium text-brand-700">
                                    <Download className="h-3 w-3" aria-hidden="true" />
                                    {t('td.download')}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ),
                )}
                </>
              )}
            </div>
          )}

          {/* Upload progress banner — positioned next to conversation context */}
          {pendingUploads.length > 0 && (
            <Card className={failed.length > 0 ? 'border-danger-500/20 bg-danger-50' : 'border-brand-200 bg-brand-50'}>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm">
                    {uploading.length > 0 && (
                      <>
                        <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" role="status" aria-label={t('ticketDetail.uploadingAria')} />
                        <span className="text-brand-700">
                          {t('ticketDetail.uploadingProgress', { uploading: uploading.length, total: pendingUploads.length })}
                        </span>
                      </>
                    )}
                    {uploading.length === 0 && failed.length > 0 && (
                      <span className="text-danger-600">
                        {t('ticketDetail.uploadsFailed', { count: failed.length })}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {failed.length > 0 && ticketId && (
                      <Button variant="ghost" size="sm" onClick={() => retryFailed(ticketId)}>
                        {t('ticketDetail.retry')}
                      </Button>
                    )}
                    {ticketId && uploading.length === 0 && (
                      <Button variant="ghost" size="sm" onClick={() => dismiss(ticketId)}>
                        {t('ticketDetail.dismiss')}
                      </Button>
                    )}
                  </div>
                </div>
                {failed.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {failed.map((u, i) => (
                      <p key={i} className="text-xs text-danger-600">
                        {u.file.name}: {u.errorMessage}
                      </p>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Reply Composer */}
          {tab === 'conversations' && ticketId && (
            <ReplyComposer ticketId={ticketId} onSent={handleReplySent} />
          )}

          {/* Activities timeline — canonical TicketEvent rows are rendered first;
              unmatched legacy ActivityLog rows remain visible so the count and
              the timeline always describe the same set of entries. */}
          {tab === 'activity' && (
            <div className="space-y-2">
              {activityEntries.length === 0 ? (
                <EmptyState
                  title={t('ticketDetail.noActivityYet')}
                  description={t('ticketDetail.noActivityDescription')}
                />
              ) : (
                activityEntries.map((entry) => (
                  <div
                    key={entry.id}
                    className="flex items-start gap-3 rounded-md bg-surface-hover px-3 py-2 text-sm"
                  >
                    <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-subtle" aria-hidden="true" />
                    <div className="flex-1">
                      <p className="text-ink-muted">{entry.description}</p>
                      <p className="mt-0.5 text-xs text-ink-subtle">{formatDateTime(entry.createdAt, user?.timezone)}</p>
                    </div>
                    <Badge variant="neutral" className="shrink-0">{entry.tag}</Badge>
                  </div>
                ))
              )}
            </div>
          )}
          </div>
        </div>

        {/* Properties pane */}
        <aside
          className={cn(
            'min-h-0 shrink-0',
            propertiesCollapsed
              ? 'w-10'
              : 'border-l border-line bg-surface-page w-[min(22rem,28vw)]',
          )}
          aria-label={t('td.propertiesAria')}
        >
          {propertiesCollapsed ? (
            <div className="flex h-full items-start justify-center pt-3">
              <button
                type="button"
                onClick={() => setPropertiesCollapsed(false)}
                aria-label={t('td.expandProperties')}
                className="rounded-md p-2 text-ink-subtle hover:bg-surface-hover hover:text-ink"
              >
                <PanelLeftOpen className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          ) : (
            <div className="flex h-full min-h-0 flex-col">
              <div className="flex shrink-0 justify-end border-b border-line px-2 py-2">
                <button
                  type="button"
                  onClick={() => setPropertiesCollapsed(true)}
                  aria-label={t('td.collapseProperties')}
                  className="rounded-md p-1.5 text-ink-subtle hover:bg-surface-hover hover:text-ink"
                >
                  <PanelLeftClose className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-3">
          {/* SLA / status header — reference places this at the very top */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm">{t('ticketDetail.slaLabel')}</CardTitle>
                {can('sla_edit') && !slaEditing && (
                  <button
                    type="button"
                    onClick={() => openSlaEditor()}
                    aria-label={t('ticketDetail.editButton')}
                    className="rounded p-1.5 text-ink-subtle hover:bg-surface-hover hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                  >
                    <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {slaEditing && (
                <div className="space-y-3 rounded-md border border-line bg-surface-hover p-3">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-ink-subtle" htmlFor="sla-metric">{t('td.metric')}</label>
                    <select
                      id="sla-metric"
                      value={slaMetric}
                      onChange={(event) => openSlaEditor(event.target.value as SlaOverrideMetric)}
                      className="w-full rounded border border-line-strong bg-surface px-2 py-1.5 text-sm text-ink"
                      disabled={slaSaving}
                    >
                      <option value="first_response">{t('td.firstResponseOption')}</option>
                      <option value="resolution">{t('td.resolutionOption')}</option>
                    </select>
                  </div>
                  <p className="text-xs text-ink-subtle">{t('td.overrideHint')}</p>
                  {slaMutationError && (
                    <p role="alert" className="rounded border border-danger-500/20 bg-danger-50 px-2 py-1.5 text-xs text-danger-700">
                      {slaMutationError}
                    </p>
                  )}
                  {/* Quick relative presets — the fast path. */}
                  <div className="flex flex-col gap-1">
                    {SLA_PRESETS.map((preset) => (
                      <button
                        key={preset.key}
                        type="button"
                        disabled={slaSaving}
                        onClick={() => applySlaPreset(preset)}
                        className="flex items-center justify-between rounded px-2 py-1.5 text-left text-sm hover:bg-surface disabled:opacity-50"
                      >
                        <span>{t(preset.labelKey)}</span>
                        <span className="text-xs text-ink-subtle">
                          {preset.resolve(new Date()).toLocaleString(language, {
                            day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
                          })}
                        </span>
                      </button>
                    ))}
                    <button
                      type="button"
                      disabled={slaSaving}
                      onClick={() => setSlaCustom((v) => !v)}
                      aria-expanded={slaCustom}
                      className="rounded px-2 py-1.5 text-left text-sm hover:bg-surface disabled:opacity-50"
                    >
                      {t('ticketDetail.pickDateTime')}
                    </button>
                  </div>

                  {slaCustom && (
                    <div>
                      <label className="mb-1 block text-xs font-medium text-ink-subtle" htmlFor="sla-due">{t('td.deadline')}</label>
                      <input
                        id="sla-due"
                        type="datetime-local"
                        value={slaDueBy}
                        onChange={(event) => setSlaDueBy(event.target.value)}
                        className="w-full rounded border border-line px-2 py-1.5 text-sm"
                      />
                    </div>
                  )}
                  <label className="block">
                    <span className="mb-1 block text-xs font-medium text-ink-subtle">{t('td.reasonOptional')}</span>
                    <input
                      value={slaReason}
                      maxLength={500}
                      onChange={(event) => setSlaReason(event.target.value)}
                      placeholder={t('td.reasonPlaceholder')}
                      className="w-full rounded border border-line px-2 py-1.5 text-sm"
                    />
                  </label>

                  <div className="flex flex-wrap gap-2 border-t border-line pt-3">
                    {slaMetric === 'resolution' && obligationForMetric('resolution')?.isOverridden && (
                      <Button variant="outline" size="sm" disabled={slaSaving} onClick={() => void clearSlaTarget('resolution')}>
                        {t('td.clearResolutionOverride')}
                      </Button>
                    )}
                    {obligationForMetric('resolution')?.escalationAt && (
                      <Button variant="outline" size="sm" disabled={slaSaving} onClick={() => void clearSlaTarget('escalation')}>
                        {t('td.clearEscalationTarget')}
                      </Button>
                    )}
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" disabled={slaSaving} onClick={() => setSlaEditing(false)}>
                      {t('common.cancel')}
                    </Button>
                    {slaCustom && (
                      <Button size="sm" disabled={slaSaving || !slaDueBy} onClick={() => void handleSlaOverride()}>
                        {slaSaving ? t('ticketDetail.savingSla') : t('ticketDetail.saveSla')}
                      </Button>
                    )}
                  </div>
                </div>
              )}
              <SlaClockPanel
                state={slaState}
                loading={slaLoading}
                error={slaError}
                stale={slaStale}
                timezone={user?.timezone}
                onRetry={() => loadTicket()}
              />
            </CardContent>
          </Card>
          {/* Properties */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">{t('ticketDetail.properties')}</CardTitle>
            </CardHeader>
            {!propertiesCollapsed && (
              <>
                <CardContent className="space-y-3 text-sm">
                  <DynamicTicketFieldRenderer
                    fields={sidebarFields}
                    values={fieldValues}
                    errors={fieldErrors}
                    mode="agent"
                    onChange={setFieldEdit}
                  />
                  {ticket.tags.length > 0 && (
                    <div>
                      <label className="mb-1 block text-xs font-medium text-ink-subtle">{t('ticketDetail.tags')}</label>
                      <TagList tags={ticket.tags.map((tag) => tag.tag.name)} />
                    </div>
                  )}
                </CardContent>
              </>
            )}
          </Card>
              </div>
              <div className="shrink-0 border-t border-line bg-surface-page px-4 py-3">
                <Button
                  className="w-full"
                  size="sm"
                  disabled={Object.keys(edits).length === 0 || saving}
                  onClick={handleUpdate}
                >
                  {saving ? t('ticketDetail.updating') : t('ticketDetail.update')}
                </Button>
              </div>
            </div>
          )}
        </aside>

        {/* Tools and contact sidebar */}
        <aside
          className={cn(
            'flex min-h-0 shrink-0 border-l border-line bg-surface-page',
            sidebarCollapsed ? 'w-11' : 'w-[min(22rem,28vw)]',
          )}
          aria-label={t('td.toolsAria')}
        >
          <div className="min-h-0 flex-1 overflow-y-auto">
          {/* Single continuous panel of collapsed rows — matches the
              Contact info / KB / Linked Tickets / Dates / Atlassian Jira
              Plus stack in the reference screenshots. */}
          {!sidebarCollapsed && (
            <Card className="overflow-hidden py-0">
            <div className="flex items-center justify-between border-b border-line px-4 py-2.5">
                <span className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">{t('td.ticketContext')}</span>
              </div>
              <SidebarRow icon={UserRound} label={t('ticketDetail.requester')} open={sidebarRowOpen.contact} onToggle={() => toggleSidebarRow('contact')}>
              <div className="rounded bg-surface px-2.5 py-2">
                <span className="font-medium text-ink">
                  {ticket.requester?.firstName} {ticket.requester?.lastName}
                </span>
                <p className="mt-0.5 text-xs text-ink-subtle">{ticket.requester?.email}</p>
              </div>
            </SidebarRow>

            <SidebarRow icon={BookOpen} label={t('td.knowledgeBase')} open={sidebarRowOpen.kb} onToggle={() => toggleSidebarRow('kb')}>
              {ticketId && <SuggestedArticles ticketId={ticketId} bare />}
            </SidebarRow>

            <SidebarRow
              icon={TicketIcon}
              label={t('ticketDetail.relationships')}
              open={sidebarRowOpen.relationships}
              onToggle={() => toggleSidebarRow('relationships')}
              badge={ticket.childTickets.length + (ticket.parentTicket ? 1 : 0) > 0 ? (
                <Badge variant="neutral">{ticket.childTickets.length + (ticket.parentTicket ? 1 : 0)}</Badge>
              ) : undefined}
            >
              <div className="space-y-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-ink-subtle">{t('ticketDetail.parentTicket')}</label>
                  {ticket.parentTicket ? (
                    <div className="flex items-center justify-between rounded bg-surface px-2 py-1.5">
                      <button
                        type="button"
                        className="truncate text-left text-brand-700 hover:underline"
                        onClick={() => navigate(`/tickets/${ticket.parentTicket!.id}`)}
                      >
                        #{ticket.parentTicket.ticketNumber} · {ticket.parentTicket.subject}
                      </button>
                      {can('edit') && (
                        <button type="button" className="ml-2 shrink-0 text-xs text-ink-subtle hover:text-danger-600" onClick={() => void setParentTicket(null)}>
                          {t('ticketDetail.unlink')}
                        </button>
                      )}
                    </div>
                  ) : <p className="text-xs text-ink-subtle">{t('ticketDetail.noParentTicket')}</p>}
                </div>

                <div>
                  <label className="mb-1 block text-xs font-medium text-ink-subtle">{t('ticketDetail.childTicketsCount', { count: ticket.childTickets.length })}</label>
                  {ticket.childTickets.length > 0 ? (
                    <div className="space-y-1">
                      {ticket.childTickets.map((child) => (
                        <button
                          key={child.id}
                          type="button"
                          className="block w-full truncate rounded bg-surface px-2 py-1.5 text-left text-brand-700 hover:bg-brand-50 hover:underline"
                          onClick={() => navigate(`/tickets/${child.id}`)}
                        >
                          #{child.ticketNumber} · {child.subject}
                        </button>
                      ))}
                    </div>
                  ) : <p className="text-xs text-ink-subtle">{t('ticketDetail.noChildTickets')}</p>}
                </div>

                {can('edit') && (
                  <>
                    <div className="border-t border-line pt-3">
                      <label className="mb-1 block text-xs font-medium text-ink-subtle">{t('ticketDetail.findTicket')}</label>
                      <div className="flex gap-2">
                        <input
                          value={relationshipQuery}
                          onChange={(event) => setRelationshipQuery(event.target.value)}
                          onKeyDown={(event) => { if (event.key === 'Enter') void searchRelationshipTickets(); }}
                          placeholder={t('ticketDetail.ticketSearchPlaceholder')}
                          className="min-w-0 flex-1 rounded border border-line-strong px-2 py-1.5 text-xs"
                        />
                        <Button variant="outline" size="sm" disabled={relationshipBusy || relationshipQuery.trim().length < 2} onClick={() => void searchRelationshipTickets()}>
                          {t('ticketDetail.searchButton')}
                        </Button>
                      </div>
                      {relationshipResults.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {relationshipResults.map((candidate) => (
                            <div key={candidate.id} className="rounded border border-line px-2 py-1.5 text-xs">
                              <div className="truncate font-medium">#{candidate.ticketNumber} · {candidate.subject}</div>
                              <div className="mt-1 flex gap-2">
                                <button type="button" className="text-brand-700 hover:underline" disabled={relationshipBusy} onClick={() => void setParentTicket(candidate.id)}>{t('ticketDetail.setParent')}</button>
                                <button type="button" className="text-danger-600 hover:underline" disabled={relationshipBusy} onClick={() => void mergeInto(candidate)}>{t('ticketDetail.mergeIntoAction')}</button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="border-t border-line pt-3">
                      <label className="mb-1 block text-xs font-medium text-ink-subtle">{t('ticketDetail.splitIntoChild')}</label>
                      <div className="flex gap-2">
                        <input
                          value={splitSubject}
                          onChange={(event) => setSplitSubject(event.target.value)}
                          placeholder={t('ticketDetail.newTicketSubjectPlaceholder')}
                          className="min-w-0 flex-1 rounded border border-line-strong px-2 py-1.5 text-xs"
                        />
                        <Button variant="outline" size="sm" disabled={relationshipBusy || !splitSubject.trim()} onClick={() => void splitTicket()}>
                          {t('ticketDetail.splitButton')}
                        </Button>
                      </div>
                      <div className="mt-2 space-y-1.5 text-xs text-ink-muted">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={copySplitAttachments}
                            onChange={(event) => setCopySplitAttachments(event.target.checked)}
                            disabled={relationshipBusy}
                          />
                          {t('ticketDetail.copyAttachments')}
                        </label>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={copySplitConversations}
                            onChange={(event) => setCopySplitConversations(event.target.checked)}
                            disabled={relationshipBusy}
                          />
                          {t('ticketDetail.copyConversationHistory')}
                        </label>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </SidebarRow>

            {can('jira_link') && ticketId && (
              <SidebarRow
                icon={Puzzle}
                label={t('nav.jira')}
                open={sidebarRowOpen.jira}
                onToggle={() => toggleSidebarRow('jira')}
              >
                <JiraLinksCard
                  ticketId={ticketId}
                  ticketSubject={ticket.subject}
                  ticketDescription={ticket.description}
                />
              </SidebarRow>
            )}

            <SidebarRow icon={CalendarClock} label={t('ticketDetail.dates')} open={sidebarRowOpen.dates} onToggle={() => toggleSidebarRow('dates')}>
              <div className="space-y-1 text-xs text-ink-muted">
                <div className="flex justify-between">
                  <span>{t('ticketDetail.created')}</span>
                  <span>{formatDateTime(ticket.createdAt, user?.timezone)}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('ticketDetail.updated')}</span>
                  <span>{formatDateTime(ticket.updatedAt, user?.timezone)}</span>
                </div>
                {ticket.resolvedAt && (
                  <div className="flex justify-between">
                    <span>{t('ticketDetail.resolved')}</span>
                    <span>{formatDateTime(ticket.resolvedAt, user?.timezone)}</span>
                  </div>
                )}
                {ticket.closedAt && (
                  <div className="flex justify-between">
                    <span>{t('ticketDetail.closed')}</span>
                    <span>{formatDateTime(ticket.closedAt, user?.timezone)}</span>
                  </div>
                )}
              </div>
            </SidebarRow>
            </Card>
          )}
          </div>

          <aside className="flex h-full w-11 shrink-0 flex-col items-center gap-1 border-l border-line bg-surface p-1.5" aria-label={t('td.shortcutsAria')}>
            <Tooltip label={sidebarCollapsed ? t('td.showContext') : t('td.hideContext')}>
              <button
                type="button"
                onClick={() => setSidebarCollapsed((value) => !value)}
                aria-label={sidebarCollapsed ? t('td.showContext') : t('td.hideContext')}
                className="mb-1 rounded-md p-2 text-ink-subtle hover:bg-surface-hover hover:text-ink"
              >
                {sidebarCollapsed ? (
                  <PanelRightOpen className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <PanelRightClose className="h-4 w-4" aria-hidden="true" />
                )}
              </button>
            </Tooltip>
            <div className="h-px w-full bg-line" />
            {[
              { key: 'contact', label: t('ticketDetail.requester'), Icon: UserRound },
              { key: 'kb', label: t('td.knowledgeBase'), Icon: BookOpen },
              { key: 'relationships', label: t('ticketDetail.relationships'), Icon: TicketIcon, badge: ticket.childTickets.length + (ticket.parentTicket ? 1 : 0) },
              { key: 'dates', label: t('ticketDetail.dates'), Icon: CalendarClock },
            ].map(({ key, label, Icon, badge }) => (
              <button
                key={key}
                type="button"
                onClick={() => openSidebarRow(key)}
                aria-label={t('td.openRow', { label })}
                aria-pressed={!sidebarCollapsed && !!sidebarRowOpen[key]}
                className={cn(
                  'relative rounded-md p-2 text-ink-subtle hover:bg-brand-50 hover:text-brand-700',
                  !sidebarCollapsed && sidebarRowOpen[key] && 'bg-brand-50 text-brand-700',
                )}
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
                {badge ? <span className="absolute -right-0.5 -top-0.5 min-w-3 rounded-full bg-brand-600 px-0.5 text-center text-[9px] leading-3 text-white">{badge}</span> : null}
              </button>
            ))}
            {can('jira_link') && (
              <button
                type="button"
                onClick={() => openSidebarRow('jira')}
                aria-label={t('td.openRow', { label: t('nav.jira') })}
                aria-pressed={!sidebarCollapsed && !!sidebarRowOpen.jira}
                className={cn(
                  'relative rounded-md p-2 text-ink-subtle hover:bg-brand-50 hover:text-brand-700',
                  !sidebarCollapsed && sidebarRowOpen.jira && 'bg-brand-50 text-brand-700',
                )}
              >
                <Puzzle className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
          </aside>
        </aside>
      </div>

      <ThreadDrawer
        ticketId={ticketId ?? ''}
        conversationId={threadConversationId}
        onClose={() => setThreadConversationId(null)}
        timezone={user?.timezone}
      />

      <ForwardDrawer
        ticketId={ticketId ?? ''}
        conversation={forwardConversation}
        conversations={displayedConversations}
        onClose={() => setForwardConversation(null)}
        onForwarded={loadTicket}
        timezone={user?.timezone}
      />

      <SlideOver
        open={descriptionOpen}
        onClose={() => setDescriptionOpen(false)}
        title={t('td.editTicket')}
        widthClassName="max-w-2xl"
        footer={(
          <div className="flex justify-end gap-2">
            <Button variant="ghost" disabled={descriptionSaving} onClick={() => setDescriptionOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button disabled={descriptionSaving || !editSubjectDraft.trim()} onClick={() => void saveDescription()}>
              {descriptionSaving ? t('common.saving') : t('common.save')}
            </Button>
          </div>
        )}
      >
        <div className="space-y-4 p-5">
          <SearchableSelect
            label={t('tickets.columnContact')}
            options={editContactOptions}
            value={editContactId}
            onChange={setEditContactId}
            onSearch={handleEditContactSearch}
            loading={editContactLoading}
            placeholder={t('td.searchContactPlaceholder')}
          />
          <div>
            <label className="mb-1 block text-sm font-medium text-ink-muted">{t('tickets.columnSubject')}</label>
            <input
              value={editSubjectDraft}
              onChange={(event) => setEditSubjectDraft(event.target.value)}
              disabled={descriptionSaving}
              className="w-full rounded-md border border-line-strong px-3 py-2 text-sm"
            />
          </div>
          <RichTextEditor
            value={descriptionDraft}
            onChange={setDescriptionDraft}
            label={t('fields.label.description')}
            placeholder={t('td.describePlaceholder')}
            minHeight="280px"
            disabled={descriptionSaving}
          />
          <div>
            <p className="mb-2 text-xs font-medium text-ink-subtle">
              {t('ticketDetail.attachmentsCount', { count: ticket?.attachments.length ?? 0 })}
            </p>
            {ticket && ticket.attachments.length > 0 ? (
              <div className="space-y-1.5">
                {ticket.attachments.map((att) => (
                  <div key={att.id} className="flex items-center justify-between rounded-md border border-line px-3 py-2 text-sm">
                    <span className="truncate">{att.fileName}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="rounded-md border border-dashed border-line px-3 py-2 text-sm text-ink-subtle">{t('td.noAttachments')}</p>
            )}
          </div>
        </div>
      </SlideOver>

      <ExportTicketsDrawer
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        filters={{}}
        ticketIds={ticketId ? [ticketId] : []}
      />
    </div>
  );
}
